import React, { useMemo } from 'react';
import dayjs from 'dayjs';

import {
  Calendar as ReactBigCalendar,
  dayjsLocalizer,
  type Event,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { getContrastText } from 'utils/stringHelper';

import type { CampaignType } from 'types/campaignTypes';
import { CHANNEL_COLORS } from '../../constants';
import CalendarContainer, { BoxWithIcon } from './Calendar.style';

type PropsType = {
  campaigns: CampaignType[];
  selectedEvent: CampaignType | null;
  onSelectEvent?: (event: CampaignType) => void;
} & React.ComponentPropsWithoutRef<'div'>;

const localizer = dayjsLocalizer(dayjs);

type CustomEventsType = Event & {
  color: string;
};

const makeTitle = (item: CampaignType) => {
  if (item.status === 'paused') {
    return (
      <BoxWithIcon>
        <PauseCircleIcon /> <span>{item.name}</span>
      </BoxWithIcon>
    );
  }
  if (item.status === 'deleted') {
    return (
      <BoxWithIcon>
        <DeleteForeverIcon /> <span>{item.name}</span>
      </BoxWithIcon>
    );
  }
  return item.name;
};

const makeColor = (item: CampaignType) => {
  if (item.status === 'paused') {
    return '#c9ff03';
  }
  if (item.status === 'deleted') {
    return '#ff0000';
  }
  return CHANNEL_COLORS[item.channel];
};

const Calendar: React.FC<PropsType> = ({
  campaigns,
  selectedEvent,
  onSelectEvent,
}) => {
  const eventsList = useMemo(() => {
    return campaigns.map((item) => ({
      title: makeTitle(item),
      start: new Date(item.scheduledAt),
      end: new Date(item.scheduledAt),
      color: makeColor(item),
      resource: item,
      type: 'campaign',
    }));
  }, [campaigns]);

  const selected = useMemo(() => {
    return eventsList.find((item) => item.resource?.id === selectedEvent?.id) || null;
  }, [selectedEvent, eventsList]);

  const handleSelectEvent = (item: CustomEventsType) => {
    onSelectEvent?.(item.resource);
  };

  return (
    <CalendarContainer>
      <ReactBigCalendar
        localizer={localizer}
        events={eventsList}
        startAccessor="start"
        endAccessor="end"
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.color,
            color: getContrastText(event.color),
          },
        })}
        onSelectEvent={handleSelectEvent}
        selected={selected}
      />
    </CalendarContainer>
  );
};

export default Calendar;
